import { useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useRouterState } from '@tanstack/react-router';
import { posthog } from '@/lib/posthog';
import { useAuth } from '@/hooks/use-auth';
import { useConversation } from '@/hooks/use-conversations-v2';
import { useModel } from '@/hooks/use-models-v2';
import {
  usePublicDeal,
  usePublicDealConversation,
  useSharedModelView,
} from '@/hooks/use-public-deal';
import { useSharedStreaming } from '@/hooks/use-shared-streaming';
import { getViewerOverrides } from '@/hooks/use-viewer-overrides';
import { useConversationAccess } from '@/hooks/use-conversation-access';
import type { ModelSchema } from '@arcanum-hq/shared-types';
import {
  PublicChromeShell,
  UnlockFullArcanumAccessPrompt,
  DealTabBar,
  DealModelView,
  PublicDealChatThread,
  authUrlWithShare,
  type DealTab,
} from '@/components/deal';
import {
  NarrativeBar,
  type NarrativeBarShareMode,
} from '@/components/layout/NarrativeBar';
import { AdminViewingBanner } from '@/components/builder';
import {
  SignalOperatingAsset,
  SignalNewDevelopment,
  SignalAnchorListener,
} from '@/components/builder/signal';
import { ApiError } from '@/lib/api';
import {
  SIGNAL_SECTION_IDS,
  type SignalSectionId,
} from '@/lib/deep-link-resolution';

/**
 * `agent-ux-optimization` Phase 6 Session 6 (2026-05-08).
 *
 * `/deal/:shareId` — public deal-link surface.
 *
 * Authentication contract (I3 from the design.md):
 *
 *   - Anonymous viewer → render `<PublicChromeShell>` + variant body.
 *   - Authenticated viewer with conversation access → redirect to
 *     `/builder?conversation=<id>&tab=signal` (full session).
 *   - Authenticated viewer WITHOUT access (RLS denies because they're
 *     signed in under a different account from the deal owner) → render
 *     `<PublicChromeShell>` + variant body. Do NOT 403 — the deal link
 *     is intentionally usable across tenant boundaries.
 *
 * Variant dispatch mirrors the authenticated builder: the underlying
 * conversation's pinned model carries `signal_variant`, which selects
 * `<SignalOperatingAsset>` vs `<SignalNewDevelopment>`. Public viewers
 * get an 8-card paginated Assumption Integrity grid (vs 4 for auth) per
 * the blueprint goal.
 *
 * The shell is intentionally minimal-chrome (no Sidebar, no Header, no
 * MarketTicker, no NarrativeBar). The Session 7 NarrativeBar migration
 * adds a gated NarrativeBar here; for now Session 6 omits it.
 */
export function DealPage() {
  const { shareId } = useParams({ strict: false }) as { shareId: string };
  const { user, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();

  // NEW Phase 3 § 3.4 — PostHog product event: deal link viewed.
  // StrictMode ref-dedupe: fires exactly once per real mount.
  // Anonymous-viewer-safe (posthog-js auto-assigns distinct_id cookie).
  const dealLinkViewedFiredRef = useRef(false);
  useEffect(() => {
    if (dealLinkViewedFiredRef.current) return;
    dealLinkViewedFiredRef.current = true;
    posthog.capture('deal_link_viewed', { share_id: shareId });
  }, [shareId]);
  // `external-sharing` Phase 5 Session 5.3 + 5.4 (2026-05-09; revised
  // post-Pass-3-walkthrough 2026-05-09) — search params for
  // `?tab=signal|model` and `?preview=true`. The deal route doesn't
  // declare a validateSearch schema (Phase 6 left it open so
  // PublicDealAnchorListener can read `?section=`/`?assumption=` via
  // window.location.search without schema-stripping). TanStack Router's
  // `useSearch({ strict: false })` returns `{}` for routes without a
  // schema — it doesn't auto-parse the URL's query string.
  //
  // `useRouterState` subscribes to the location, so this re-renders
  // when navigate() updates the URL (tab clicks, deep-link consumption,
  // etc.) — that reactivity is essential. Then parse the search string
  // via URLSearchParams. Same pattern PublicDealAnchorListener uses
  // for `?section`/`?assumption`, but with reactive subscription.
  const locationSearch = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const urlParams = new URLSearchParams(locationSearch);
  const tabParam = urlParams.get('tab');
  const requestedTab: DealTab | undefined =
    tabParam === 'signal' || tabParam === 'model' ? tabParam : undefined;
  // `?preview=true` opt-in bypass for the owner-redirect (Session 5.4).
  // Only honored when the server-resolved viewerKind is `'owner'` —
  // anonymous viewers can't escalate by setting the param themselves
  // (the redirect-bypass branch below ANDs both conditions).
  const previewMode = urlParams.get('preview') === 'true';
  // admin read-only framing (admin-deal-oversight Phase 5). The /deal route has
  // NO validateSearch (research/phase-5.md § 9 row 7) — read raw, like previewMode.
  // Phase-3 row-click sends adminView=1 (phases/03-*.md Session 3.2).
  // ADR 0007 follow-up: gate on ACTUAL admin status — a non-admin setting
  // `?adminView=1` must NOT see the admin banner or composer suppression.
  const adminView =
    isAdmin &&
    (urlParams.get('adminView') === '1' ||
      urlParams.get('adminView') === 'true');

  const dealQuery = usePublicDeal(shareId);
  const deal = dealQuery.data;
  // Fetch the server-computed cellValues + frozen inputValues so the
  // Signal/Model tabs render real numbers instead of dashes. Server's
  // `getSharedModelView` hydrates the engine from frozen_state and
  // snapshots all formula outputs.
  const modelViewQuery = useSharedModelView(shareId);

  // Authenticated-user redirect probe — only fires when we have both a
  // signed-in user AND a resolved deal (we need the deal's
  // conversation_id to probe access). Anonymous viewers skip this
  // entirely by construction (`null` conversationId → query disabled).
  const accessProbeId = user && deal ? deal.conversationId : null;
  const { hasAccess, isLoading: accessLoading } =
    useConversationAccess(accessProbeId);

  // Authenticated builder also reads the conversation; once we know the
  // user has access and we're about to redirect, prime the title from
  // the conversation detail to avoid the brief "loading" flicker on
  // arrival. No-op when accessProbeId is null.
  useConversation(hasAccess ? accessProbeId : null);

  // Resolve the model schema for the public viewer. The deal endpoint
  // exposes `modelId`/`modelVersion`; `useModel` is reused as-is —
  // public-safe because the model registry is already
  // SELECT-public per RLS.
  const modelQuery = useModel(deal?.modelId ?? null);
  // Anonymous public viewers can't fetch `/api/models/:id` (requires
  // auth) — that endpoint 401s, leaving modelSchema null and stranding
  // the Signal hero tiles at `—`. The shared model-view endpoint
  // already returns a schema subset (inputs + outputs + dependencyGraph
  // + tableInputs + allNamedRanges), which is everything the variant
  // components actually need. Prefer it; fall back to `useModel` for
  // the rare authed-preview path (e.g. `?preview=true` for owners).
  const modelSchema =
    (modelViewQuery.data?.schema as ModelSchema | undefined) ??
    modelQuery.data?.data ??
    null;

  // deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — the prior
  // `derivedInputValues` / `derivedCellValues` / `derivedLiveMetrics`
  // useMemos + the `deriveLiveMetricsFromCellValues` helper were removed
  // when the variant prop shape collapsed onto `SignalRootProps`. Each
  // variant component now fetches its own `inputValues` + `cellValues`
  // via `useSignalFieldResolver` (auth/public-deal discriminated
  // context) — `useSharedModelView` data still flows in through this
  // route's `modelViewQuery` for the Model-tab + anchor-listener paths.

  useEffect(() => {
    if (!user || !deal || accessLoading) return;
    if (hasAccess !== true) return;
    // admin-deal-oversight ADR 0007: an admin's `hasAccess === true` comes from
    // the read-OVERRIDE (oversight), NOT ownership — so admins must NOT be
    // owner-redirected into a full Builder session. They stay on the public
    // deal page (with the AdminViewingBanner when `?adminView=1`). Without this
    // guard, the override turns every public deal link into a redirect-to-/builder
    // for admins (the "shows the workbench tab" bug).
    if (isAdmin) return;
    // `external-sharing` Phase 5 Session 5.4 (2026-05-09) — `?preview=true`
    // opt-in bypass for the owner-redirect. Only honored when the
    // server-resolved viewerKind is `'owner'` (the API guarantees this
    // discriminator only when `auth.uid() === owner_user_id`). Other
    // authed viewers with access (recipients) keep the redirect-to-builder
    // behavior — they see the full session, not the public preview.
    if (previewMode && deal.viewerKind === 'owner') return;
    navigate({
      to: '/builder',
      search: { conversation: deal.conversationId, tab: 'signal' },
      replace: true,
    });
  }, [user, deal, hasAccess, accessLoading, navigate, previewMode, isAdmin]);

  // Authenticated RECIPIENT redirect — distinct from the owner/collaborator
  // branch above. A recipient (server-resolved `viewerKind ===
  // 'internal-recipient'`) does NOT have conversation-level read access
  // (`hasAccess === false`), so the redirect above never fires for them.
  // Per the share-flow contract, a logged-in recipient who opens the public
  // deal link should land on their inbox deep-linked to this share
  // (`/shares?tab=received&highlight=<shareId>`) — that's where the Open /
  // Fork affordances live — rather than sitting on the anonymous public
  // chrome. The email link itself stays `/deal/<shareId>` so it works for
  // logged-out recipients (who see the public surface + Log-in CTA, then
  // get routed here after auth via the `?next=` we now honor in login.tsx).
  useEffect(() => {
    if (!user || !deal || accessLoading) return;
    // Owner/collaborator path is handled by the effect above (hasAccess===true).
    if (hasAccess === true) return;
    if (deal.viewerKind !== 'internal-recipient') return;
    navigate({
      to: '/shares',
      search: { tab: 'received', highlight: shareId },
      replace: true,
    });
  }, [user, deal, hasAccess, accessLoading, navigate, shareId]);

  // Loading + error states
  if (authLoading || dealQuery.isLoading) {
    return <FullScreenLoader>Loading deal…</FullScreenLoader>;
  }

  if (dealQuery.error) {
    const status =
      dealQuery.error instanceof ApiError ? dealQuery.error.status : null;
    return (
      <FullScreenError
        title={status === 404 ? 'Deal Not Found' : 'Could Not Load Deal'}
        body={
          status === 404
            ? 'This deal link no longer exists or was never valid. The deal owner may have revoked access.'
            : 'Something went wrong loading this deal. Please refresh the page or try again later.'
        }
        shareId={shareId}
      />
    );
  }

  if (!deal) {
    return <FullScreenLoader>Loading deal…</FullScreenLoader>;
  }

  // Authenticated user about to be redirected — render a minimal
  // transition state instead of flashing the public chrome. Owner-preview
  // mode (`?preview=true` AND viewerKind === 'owner') BYPASSES the
  // redirect, so this loader-flash branch must also bypass to render
  // the public chrome below.
  const inOwnerPreview = previewMode && deal.viewerKind === 'owner';
  // ADR 0007: admins are NOT owner-redirected (their access is override-read,
  // not a session) — so they must NOT show the redirect loader; fall through to
  // the public deal chrome (with the AdminViewingBanner when `?adminView=1`).
  if (user && hasAccess === true && !inOwnerPreview && !isAdmin) {
    return <FullScreenLoader>Opening in Builder…</FullScreenLoader>;
  }
  // Recipient about to be redirected to their inbox (see the
  // `internal-recipient` effect above) — render a transition state instead
  // of flashing the public chrome.
  if (
    user &&
    hasAccess !== true &&
    !accessLoading &&
    deal.viewerKind === 'internal-recipient'
  ) {
    return <FullScreenLoader>Opening your shared deals…</FullScreenLoader>;
  }

  // `external-sharing` Phase 5 Session 5.3 (2026-05-09) —
  // signal_variant=null no longer hard-fails the page. Instead, the
  // Signal tab is dimmed in `<DealTabBar>` (with a tooltip) and the
  // default tab flips to Model so the viewer still sees useful
  // content (the spreadsheet view via `<DealModelView>`).
  const signalDisabled = !deal.signalVariant;
  // Defaulting rule:
  //   - Explicit `?tab=` wins (user clicked a deep-link / refreshed).
  //   - Else if signal is disabled, default to 'model'.
  //   - Else default to 'signal' (matches Phase 6 behavior — the
  //     pre-Phase-5 page only ever showed the Signal variant).
  const activeTab: DealTab = requestedTab
    ? requestedTab
    : signalDisabled
    ? 'model'
    : 'signal';

  function handleTabChange(next: DealTab): void {
    void navigate({
      to: '/deal/$shareId',
      params: { shareId },
      search: (prev) => ({ ...prev, tab: next }),
      replace: true,
    });
  }

  // deal-signal-overhaul Phase 4 Session 5 (2026-05-12) — variant props
  // collapsed onto the new `SignalRootProps` shape. The variant components
  // now fetch their own data via `useSignalFieldResolver` (auth/public
  // discriminated context); we just thread the resolver context + the
  // surface-level UI props (title, lat/lng, pageSize, modelId/version).
  const variantProps = {
    context: { kind: 'public-deal' as const, shareId },
    // `scroll-accent` = green, thicker scrollbar on the variant body — the
    // element that actually scrolls on the public deal page — so a visitor
    // sees there's more to scroll down to (index.css). Scoped to the public
    // surface via this prop; the shared auth Signal tab doesn't pass it.
    className: 'scroll-accent',
    modelId: deal.modelId,
    modelVersion: deal.modelVersion,
    conversationTitle: deal.title,
    // Phase 6 Session 6.4 — geocoded coordinates from `usePublicDeal`'s
    // `aerialLatLng` projection. Both undefined and explicit null surface
    // as null to `<PropertyMap>`'s no-coordinates state.
    aerialLatLng: deal.aerialLatLng ?? null,
    // No live history for public shares — `<HeroReturns>` falls back to
    // sparkline-less cells.
    metricHistory: null,
    // Public viewers paginate 8 assumptions per page (vs 4 for auth) per
    // the Phase 4 acceptance criteria.
    pageSize: 8 as const,
  };

  // Resolve the 4-mode shareMode from the server's `viewerKind`
  // discriminator. Server's enum is identical to NarrativeBarShareMode
  // (`'external-anon' | 'external-authed' | 'internal-recipient' | 'owner'`)
  // so this is a typed pass-through with `isAuthed` fallback when the
  // server response lacks `viewerKind`. Pre-fix the helper used a
  // different enum guess and recipients resolved as 'external-authed' —
  // see resolveShareMode's JSDoc.
  const shareMode = resolveShareMode(deal.viewerKind, user != null);

  return (
    <PublicChromeShell
      shareId={shareId}
      shareMode={shareMode}
      tabBar={
        <DealTabBar
          activeTab={activeTab}
          onChange={handleTabChange}
          signalDisabled={signalDisabled}
        />
      }
      // Mount the composer OUTSIDE `<main>` (its own shell slot) so the
      // expanded chat overlay + the sticky collapse caret at its top aren't
      // clipped by main's `overflow-y-auto`.
      composer={
        !adminView ? (
          <PublicDealNarrativeBar shareId={shareId} shareMode={shareMode} />
        ) : undefined
      }
    >
      {activeTab === 'signal' ? (
        deal.signalVariant === 'operating-asset' ? (
          <SignalOperatingAsset {...variantProps} />
        ) : deal.signalVariant === 'new-development' ? (
          <SignalNewDevelopment {...variantProps} />
        ) : (
          // `?tab=signal` while `signal_variant === null` — the user
          // navigated here despite the dimmed tab. Surface the same
          // placeholder copy rather than a blank screen.
          <FullScreenError
            title="Signal View Unavailable"
            body="This deal's underlying model does not have a Signal variant configured. Switch to the Model tab to view the spreadsheet."
            shareId={shareId}
          />
        )
      ) : (
        <DealModelView
          shareId={shareId}
          shareMode={shareMode}
          adminView={adminView}
        />
      )}
      {adminView && (
        <AdminViewingBanner
          dealTitle={deal.title}
          ownerName={deal.ownerName ?? 'an Arcanum user'}
          className="mx-5 mt-3"
        />
      )}
      <PublicDealAnchorListener modelSchema={modelSchema} />
    </PublicChromeShell>
  );
}

/**
 * `external-sharing` Phase 5 Session 5.2 (2026-05-09; revised post-Pass-3
 * walkthrough 2026-05-09).
 *
 * Translates the server's `viewerKind` discriminator into the
 * `<NarrativeBar shareMode>` 4-mode shape. The server's enum
 * (`viewerKindSchema` in `shared-types/src/lib/sharing.ts`) is
 * **identical** to `NarrativeBarShareMode` — the function is a typed
 * pass-through with a defensive fallback for legacy callers that
 * don't populate `viewerKind`.
 *
 * Pass-through mapping:
 *   - `viewerKind` set → return verbatim
 *   - `viewerKind === undefined` → default by `isAuthed`
 *
 * Pre-fix history: an earlier version of this helper assumed the server
 * emitted `'owner' | 'authenticated_recipient' | 'anonymous'` (a guess
 * during /implement that didn't match `viewerKindSchema`). The Pass 3
 * walkthrough surfaced that internal recipients were resolving to
 * `'external-authed'` because `viewerKind === 'internal-recipient'`
 * didn't match the helper's `'authenticated_recipient'` check and the
 * defensive isAuthed branch fired instead.
 */
export function resolveShareMode(
  viewerKind:
    | 'external-anon'
    | 'external-authed'
    | 'internal-recipient'
    | 'owner'
    | undefined,
  isAuthed: boolean
): NarrativeBarShareMode {
  if (viewerKind != null) return viewerKind;
  return isAuthed ? 'external-authed' : 'external-anon';
}

/**
 * `agent-ux-optimization` Phase 6 Session 8 (2026-05-08).
 *
 * Reads `?section=` / `?assumption=` from the URL and feeds it into
 * `<SignalAnchorListener>`. Owns the consume hook that drops the params
 * after the listener fires its scroll/flash side effects. Public deal
 * page uses the same listener as the authenticated builder — variant
 * components are shared, so anchor handling is too.
 *
 * Reads URL search params via `URLSearchParams` against
 * `window.location.search` rather than TanStack Router's `useSearch`
 * because the deal route's `validateSearch` shape doesn't include
 * `section`/`assumption` (the route was registered without per-route
 * search-schema overrides; adding it would require a separate schema
 * file just for these two params). The lightweight URLSearchParams
 * read is acceptable — the page lifetime is short, deep-link arrival
 * is the only consumer, and `consumeSignalAnchor` removes the params
 * after firing.
 */
function PublicDealAnchorListener({
  modelSchema,
}: {
  modelSchema: ReturnType<typeof useModel>['data'] extends infer T
    ? T extends { data?: infer S }
      ? S | null
      : null
    : null;
}) {
  const navigate = useNavigate();
  const params =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const sectionRaw = params.get('section');
  const section = SIGNAL_SECTION_IDS.includes(sectionRaw as SignalSectionId)
    ? (sectionRaw as SignalSectionId)
    : undefined;
  const assumption = params.get('assumption') ?? undefined;

  function consume() {
    if (typeof window === 'undefined') return;
    const next = new URL(window.location.href);
    next.searchParams.delete('section');
    next.searchParams.delete('assumption');
    navigate({
      to: window.location.pathname,
      search: Object.fromEntries(next.searchParams.entries()),
      replace: true,
    });
  }

  return (
    <SignalAnchorListener
      section={section}
      assumption={assumption}
      modelSchema={
        modelSchema as Parameters<typeof SignalAnchorListener>[0]['modelSchema']
      }
      onConsume={consume}
    />
  );
}

/**
 * NarrativeBar + chat-thread wrapper for the public deal-link surface.
 *
 * `deal-signal-overhaul` Phase 5 Session 4 (2026-05-13) rewrites the
 * Phase 6 version (which used `useRecordQuestion` to bump a cookie-
 * bound counter on bare POST `/api/shared/:shareId/questions`). The
 * new shape:
 *
 *   - Mints `useSharedStreaming(shareId)` ONCE at this wrapper level
 *     so the thread + composer share one hook instance. (Session 3's
 *     `<PublicDealChatThread>` is pure-render — it receives
 *     `currentTurn` + `sendMessage` as props rather than calling the
 *     streaming hook itself, avoiding the duplicate-fetch pitfall.)
 *   - Mounts `<PublicDealChatThread>` as the FIRST child + `<NarrativeBar>`
 *     as the SECOND child inside `<PublicChromeShell>`'s body slot.
 *     The thread sits BENEATH the Signal body with a single hairline
 *     divider (Phase 4 Session 6 density contract).
 *   - `<NarrativeBar initialQuestionCount={questionCount}>` seeds from
 *     `usePublicDealConversation`. On mount this fires the gate
 *     correctly if the viewer arrives at count=3 (revisit case).
 *   - `sendGuarded(content)` is the single send path: snapshots
 *     `getViewerOverrides(shareId)` at fire time, gates on
 *     `streaming.status !== 'streaming'`, and is wired to BOTH the
 *     composer's `onSubmit` AND the chat thread's suggestion-chip
 *     `onClick`. The streaming-busy guard returns `false` so
 *     `<NarrativeBar>` does NOT clear the input nor increment the
 *     counter on a rejected submit (composer-disabled-while-streaming
 *     semantics — research § 11).
 *
 * `useRecordQuestion` is deprecated in this surface: the chat-stream
 * POST atomically increments the per-share counter server-side
 * (`share_conversations` insert + `shared_links.public_question_count`
 * bump in one transaction) so the previous double-write is redundant.
 *
 * Per-mode gate behavior carried forward from `external-sharing` Phase 5
 * Session 5.2 — see `resolveShareMode` above for the matrix.
 */
export function PublicDealNarrativeBar({
  shareId,
  shareMode,
}: {
  shareId: string;
  shareMode: NarrativeBarShareMode;
}) {
  const deal = usePublicDeal(shareId);
  const conversation = usePublicDealConversation(shareId);
  const streaming = useSharedStreaming(shareId);

  // `deal-signal-overhaul` Phase 5 redesign (2026-05-12): the bar-internal
  // gate-swap (`maxQuestions` + `gateContent`) is REPLACED by an in-thread
  // "From Arcanum" system message inside `<PublicDealChatThread>`. The
  // composer stays in place but reads as disabled with the "Sign up to
  // continue…" placeholder. Unified across anon + authed external viewers.
  // Internal recipients + owners aren't gated server-side.
  const gateActive =
    (shareMode === 'external-anon' || shareMode === 'external-authed') &&
    (conversation.data?.gateActive ?? false);

  // Composer is busy from the moment a send is dispatched (`connecting`)
  // through the live `streaming` window, until the turn terminates
  // (`done`/`error`/`aborted`). While busy the whole bar reads as disabled
  // (input + send + chips) so the visitor can't fire a concurrent question
  // while Arcanum is still answering — the `sendGuarded` streaming guard
  // below is the logical backstop; this is the visual lock.
  const streamingBusy =
    streaming.status === 'connecting' || streaming.status === 'streaming';

  // Single send path used by BOTH the composer's `onSubmit` AND the
  // chat thread's suggestion-chip `onClick`. Streaming-busy guard
  // rejects concurrent submissions; gate-active short-circuit prevents
  // any further sends once the in-thread gate message is visible. The
  // `<NarrativeBar>` reads the `false` return value and skips its
  // setInput('') + setQuestionCount bumps so the composer state stays
  // consistent across the stream.
  const sendGuarded = useCallback(
    (content: string): boolean => {
      if (gateActive) return false;
      if (streaming.status === 'connecting' || streaming.status === 'streaming')
        return false;
      const overrides = getViewerOverrides(shareId);
      streaming.sendMessage(
        content,
        Object.keys(overrides).length > 0 ? overrides : undefined
      );
      return true;
    },
    [shareId, streaming, gateActive]
  );

  // WS2 (R-WEB3): the NarrativeBar `expandedSuggestions` is the SINGLE
  // always-present chip owner. Live set supersedes the turn-0 seed —
  // `streaming.recomputedChips` is pushed by the hook on each `done` (consumes
  // the additive `done.suggestionChips`); before the first AI turn it's null and
  // we fall back to `usePublicDeal().data.suggestionChips` (frozen at share
  // creation). Passed to the bar as `expandedSuggestions` with the high-contrast
  // variant so the starter questions read as the primary on-ramp.
  const liveChips =
    streaming.recomputedChips ?? deal.data?.suggestionChips ?? null;

  // `<NarrativeBar>` mount is gated on `conversation.data != null` because the
  // bar treats `initialQuestionCount` as a SEED only (uncontrolled invariant —
  // `apps/web/src/components/layout/NarrativeBar.tsx:152-176`). If we mount
  // the bar before the conversation query resolves, `initialQuestionCount`
  // gets the `?? 0` fallback and the local counter sticks at 0 forever.
  //
  // `deal-signal-overhaul` Phase 5 overlay redesign (2026-05-12) — the chat
  // thread is no longer a sibling of the bar. It rides inside the bar's
  // `renderAboveComposer` slot (which the bar overlays as an absolute-
  // positioned popover when `expanded === true`). Signal content stays
  // exactly where it is when collapsed; the thread floats over it on
  // expand. Suggestion chips are passed via `expandedSuggestions` and
  // render inside the overlay alongside the thread.
  return conversation.data != null ? (
    <NarrativeBar
      mode="prompt"
      shareMode={shareMode}
      headerBadge="SHARED DEAL"
      dealName={deal.data?.title}
      initialQuestionCount={conversation.data.questionCount}
      questionBudget={3}
      onSubmit={(content) => sendGuarded(content)}
      expandedSuggestions={!gateActive ? liveChips?.chips ?? [] : []}
      chipVariant="high-contrast"
      onSuggestionClick={(chip) => {
        void sendGuarded(chip);
      }}
      disabled={gateActive || streamingBusy}
      disabledPlaceholder={
        gateActive ? 'Sign up to continue…' : 'Arcanum is responding…'
      }
      renderAboveComposer={(expanded) =>
        expanded ? (
          <PublicDealChatThread
            shareId={shareId}
            shareMode={shareMode}
            suggestionChips={deal.data?.suggestionChips ?? null}
            currentTurn={streaming.currentTurn}
            pendingHuman={streaming.pendingHuman}
            sendMessage={sendGuarded}
          />
        ) : null
      }
    />
  ) : null;
}

function FullScreenLoader({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-testid="public-deal-loader"
      className="flex flex-col items-center justify-center h-screen bg-void gap-3"
    >
      <span className="text-sm font-mono text-text-dim">{children}</span>
    </div>
  );
}

function FullScreenError({
  title,
  body,
  shareId,
}: {
  title: string;
  body: string;
  /** When present, the Unlock CTA carries the shareId for post-auth linking. */
  shareId?: string;
}) {
  return (
    <div
      data-testid="public-deal-error"
      className="flex-1 flex flex-col items-center justify-center gap-4 px-6 py-12 bg-void"
    >
      <h1 className="text-lg font-mono font-semibold text-text-bright tracking-[0.3px]">
        {title}
      </h1>
      <p className="text-sm font-mono text-text-muted text-center max-w-md">
        {body}
      </p>
      <UnlockFullArcanumAccessPrompt
        className="max-w-md w-full"
        signupHref={authUrlWithShare('/signup', shareId)}
        onSignupClick={() =>
          posthog.capture('deal_signup_cta_clicked', {
            source: 'error-unlock',
            share_id: shareId,
          })
        }
      />
    </div>
  );
}
